import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrandLogo } from '../components/BrandLogo';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { checkoutErrText } from '../lib/subscriptionCheckout';
import { readRefCode } from '../lib/influencerTracking';

export function CheckoutPage() {
  const { user, loading, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [priceLabel, setPriceLabel] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');

  useEffect(() => {
    void api<{ displayPrice?: string; amount?: number }>('/api/stripe/price-info')
      .then((p) => {
        if (p.displayPrice) setPriceLabel(p.displayPrice);
        else if (p.amount != null) setPriceLabel(`$${(p.amount / 100).toFixed(2)}/mo`);
      })
      .catch(() => setPriceLabel(null));
  }, []);

  useEffect(() => {
    try {
      const fromQuery = new URLSearchParams(window.location.search).get('promo')?.trim();
      if (fromQuery) setPromoCode(fromQuery);
    } catch {
      /* ignore */
    }
  }, []);

  if (loading) {
    return <div className="app-body flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/signup" replace />;
  if (!user.onboardingCompleted) return <Navigate to="/onboarding" replace />;
  if (user.aiCoachingEnabled) return <Navigate to="/download" replace />;

  async function startCheckout(e?: FormEvent) {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const ref = readRefCode();
      const code = promoCode.trim();
      const { url } = await api<{ url: string }>('/api/stripe/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          plan: 'coach',
          influencerCode: ref || undefined,
          promoCode: code || undefined,
        }),
      });
      if (!url) throw new Error('Checkout could not start.');
      window.location.href = url;
    } catch (e) {
      setErr(e instanceof ApiError ? checkoutErrText(e) : checkoutErrText(e));
      setBusy(false);
    }
  }

  return (
    <div className="app-body flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="hero" />
        </div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">Unlock Peak Performance IQ</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-muted)]">
          Subscribe to finish setup. After payment you&apos;ll get the App Store download link.
        </p>
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-left text-sm text-zinc-300">
          <p>
            <strong className="text-white">{priceLabel ?? '$7.99/month'}</strong>
            {' '}· <strong className="text-white">7-day free trial</strong>
            , then <strong className="text-white">50% off your first paid month</strong>
          </p>
          {readRefCode() ? (
            <p className="mt-2 text-sky-300/90">
              Influencer code <span className="font-mono">{readRefCode()}</span> is linked for tracking.
            </p>
          ) : null}
          <p className="mt-2 text-zinc-500">
            Log 25 days in month 1 and month 2 is automatically 30% off.
          </p>
        </div>
        <form onSubmit={(e) => void startCheckout(e)} className="mt-6 text-left">
          <label htmlFor="checkout-promo" className="block text-xs font-medium text-zinc-500">
            Promo code
          </label>
          <input
            id="checkout-promo"
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            placeholder="Leave blank for 50% founders offer"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-sky-500/40 focus:ring-1 focus:ring-sky-500/30"
          />
          {promoCode.trim() ? (
            <p className="mt-2 text-xs text-sky-300/90">
              {promoCode.trim()} will be applied instead of the founders 50% off.
            </p>
          ) : (
            <p className="mt-2 text-xs text-zinc-600">
              Leave this blank to keep 50% off your first paid month. Only enter a code if you have a different promo.
            </p>
          )}
          {err ? <p className="mt-4 text-sm text-rose-300">{err}</p> : null}
          <button
            type="submit"
            disabled={busy}
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-sky-400 px-6 py-3.5 text-sm font-semibold text-zinc-950 disabled:opacity-60"
          >
            {busy ? 'Opening Stripe…' : 'Continue to checkout'}
          </button>
        </form>
        <button
          type="button"
          className="mt-3 text-xs text-zinc-500 underline"
          onClick={() => void refresh()}
        >
          Refresh account status
        </button>
        <p className="mt-6 text-xs text-zinc-600">
          <Link to="/download" className="underline">
            Skip for now
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
